import { Input, Node, Output, Socket } from 'rete'
import { BaseComponent } from './base_component'
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { booleanDataSocket, categoricalDataSocket } from '../socket_types'
import { BooleanTileGrid, CategoricalTileGrid } from '../tile_grid'
import { isEqual } from 'lodash'

function uniqueLabel(baseLabel: string, used: Set<string>): string {
    const trimmed = baseLabel.trim() || 'Untitled data'
    if (!used.has(trimmed)) {
        used.add(trimmed)
        return trimmed
    }
    let idx = 2
    let candidate = `${trimmed} (${idx})`
    while (used.has(candidate)) {
        idx += 1
        candidate = `${trimmed} (${idx})`
    }
    used.add(candidate)
    return candidate
}

export class CategoricalComponent extends BaseComponent {
    cachedData: CategoricalTileGrid
    previousInput: Array<BooleanTileGrid>

    constructor() {
        super('Boolean to categorical dataset')
        this.category = 'Conversions'
        this.previousInput = new Array()
    }

    async builder(node: Node) {
        node.addInput(new Input('in', 'Boolean inputs', booleanDataSocket, true))

        node.addOutput(new Output('out', 'Categorical dataset', categoricalDataSocket))
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {

        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        const booleanData = (inputs['in'] || []).filter((grid): grid is BooleanTileGrid => grid instanceof BooleanTileGrid)
        if (booleanData.length === 0) {
            editorNode.meta.errorMessage = 'At least one boolean input is required'
            return
        }
        delete editorNode.meta.errorMessage

        if (isEqual(booleanData, this.previousInput)) {
            const result = this.cachedData
            result.name = editorNode.data.name as string ?? 'Categorical dataset'
            outputs['out'] = result
        } else {
            let baseBool: BooleanTileGrid | null = null
            let highestZoom = -1
            for (const boolGrid of booleanData as BooleanTileGrid[]) {
                if (boolGrid.zoom > highestZoom) {
                    highestZoom = boolGrid.zoom
                    baseBool = boolGrid
                }
            }

            if (!baseBool) { return }

            this.previousInput = booleanData as BooleanTileGrid[]

            const labels: Map<number, string> = new Map()
            const usedLabels = new Set<string>()
            const result = outputs['out'] = new CategoricalTileGrid(
                baseBool.zoom,
                baseBool.x,
                baseBool.y,
                baseBool.width,
                baseBool.height
            )

            booleanData.map((e, i) => {
                if (e instanceof BooleanTileGrid) {
                    const label = uniqueLabel(e.name ? e.name : `Untitled data ${i + 1}`, usedLabels)
                    labels.set(i + 1, label)
                    result.applyCategoryFromBooleanGrid(e, i + 1)
                }
            })

            result.setLabels(labels)
            result.name = editorNode.data.name as string ?? 'Categorical dataset'
            editorNode.data.splitLabels = Array.from(labels.entries()).map(([value, label]) => ({ value, label }))
            this.cachedData = result
        }

        editorNode.update()

    }

}